/**
 * VerifyEmail — /auth/verify/:token
 *
 * Lands here from the signup email. Auto-consumes the token on mount.
 * Shows success/failure state + a "Resend" button if the link is bad.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import BloqLogo from "@/components/BloqLogo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/email/verify/consume", { token });
        if (!mounted) return;
        setEmail(data.email || "");
        setState("ok");
        // Refresh the user object so the banner disappears immediately
        // when the user lands on this page while signed in.
        if (user) {
          try {
            await refreshUser();
          } catch {
            /* ignore */
          }
        }
      } catch (e) {
        if (!mounted) return;
        setErr(formatApiError(e) || "Could not verify this link");
        setState("error");
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [token]);

  const resend = async () => {
    if (!email && !user?.email) {
      navigate("/login");
      return;
    }
    try {
      await api.post("/auth/email/verify/send", { email: email || user.email });
      // Self-heal: refresh user state in case they were already verified
      // (e.g. a Google sign-in earlier this session marked them verified
      // but the local user object is stale). Failure here doesn't change
      // the user-visible outcome.
      try { await refreshUser?.(); } catch { /* ignore */ }
      setErr("");
      setState("resent");
    } catch (e) {
      setErr(formatApiError(e) || "Could not resend the verification email");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bloq-container pt-7">
        <BloqLogo size={26} />
      </div>
      <div className="flex-1 grid place-items-center bloq-container py-12">
        <div className="max-w-md w-full bloq-card p-8 text-center" data-testid="verify-email-card">
          {state === "verifying" && (
            <>
              <Loader2 className="w-10 h-10 text-[#005e7a] mx-auto animate-spin" />
              <p className="font-semibold mt-4">Verifying your email…</p>
            </>
          )}
          {state === "ok" && (
            <>
              <div className="w-12 h-12 rounded-full bg-[#005e7a] grid place-items-center mx-auto">
                <Check className="w-7 h-7 text-white" />
              </div>
              <p className="font-semibold mt-4 text-lg">Email verified</p>
              <p className="text-sm text-[#4a4a52] mt-2">
                Thanks for confirming{email ? <> <strong>{email}</strong></> : ""}. Your account is fully set up.
              </p>
              <button
                onClick={() => navigate(user ? "/app" : "/login")}
                className="bloq-btn-primary mt-6 px-6 py-2.5"
                data-testid="verify-continue-button"
              >
                Continue to Bloq
              </button>
            </>
          )}
          {state === "resent" && (
            <>
              <div className="w-12 h-12 rounded-full bg-[#005e7a] grid place-items-center mx-auto">
                <Check className="w-7 h-7 text-white" />
              </div>
              <p className="font-semibold mt-4 text-lg">New link sent</p>
              <p className="text-sm text-[#4a4a52] mt-2">Check your inbox for the new verification link.</p>
              <Link to="/login" className="bloq-btn-secondary inline-block mt-6 px-6 py-2.5">
                Back to sign in
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <AlertCircle className="w-10 h-10 text-[#c43838] mx-auto" />
              <p className="font-semibold mt-4 text-lg" data-testid="verify-error-text">
                {err || "This verification link is invalid or expired."}
              </p>
              <p className="text-sm text-[#4a4a52] mt-2">
                We can send you a fresh one — verification links expire after 1 hour.
              </p>
              <button
                onClick={resend}
                className="bloq-btn-primary mt-5 px-6 py-2.5"
                data-testid="verify-resend-button"
              >
                Send a new link
              </button>
              <Link to="/login" className="block text-xs text-[#82828c] mt-4">
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
